import { VisitorBotMessageResult } from "./../types";
import { EventEmitter } from "events";
import EngagementService from "../services/EngagementService";
import RealtimeService from "../services/RealtimeService";
import RealtimeServiceFactory from "../services/RealtimeServiceFactory";
import sleep from "../helpers/sleep";
import forEachAsync from "../helpers/forEachAsync";

import {
  EngagementContent,
  ChatLine,
  Message,
  EngagementContentType,
  VisitorText,
  EngagementUpdate,
  RatingUpdate,
  LineRatingUpdate,
  RealtimeState,
  LineType,
  ExitVisitorUpdate,
  VisitorBotMessage,
  VisitorBotMessageType,
  LineCreator,
  LineRating,
  MessageStatusType,
  LineVisibility,
  WebhookResult,
  SecureForm
} from "../types";
import StoreContainer from "./StoreContainer";
import { logInfo, logError } from "../helpers/eventLogger";

class ChatStore {
  private _emitter = new EventEmitter();
  private _messages: Message[] = [];
  private _chatType: "Agent" | "Bot" | null;
  private _agentName: string;
  private _agentHeadshotUrl: string;
  private _companyLogoUrl: string;
  private _agentRating: number;
  private _queuePosition: number;
  private _timeInQueue: number;
  private _estimatedWait: string;
  private _visitorTypingTimeout: any;
  private _typingAgents: any = {};
  private _typingAgentTimers: any = {};
  private _realtimeService: RealtimeService;
  private _typeaheadTimeout: any;
  private _previousTitle: string;
  private _unreadMessageCount = 0;
  private _secureForm: SecureForm;

  async connect() {
    const alreadyConnected =
      this._realtimeService &&
      this._realtimeService.getExpectedState() === RealtimeState.Connected;

    if (alreadyConnected) {
      return;
    }

    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange.bind(this)
    );
    this._emitter.emit("connecting");

    this._realtimeService = RealtimeServiceFactory.getInstance();
    this._realtimeService.onReceiveEngagementContent(
      this.handleReceiveEngagementContent.bind(this)
    );
    this._realtimeService.onReceiveSecureForm(
      this.handleReceiveSecureForm.bind(this)
    );
    this._realtimeService.onReceiveLine(this.handleReceiveLine.bind(this));

    this._realtimeService.onReceiveCobrowseRequest(
      this.handleReceiveCobrowseRequest.bind(this)
    );

    this._realtimeService.onReceiveStopCobrowsing(
      this.handleStopCobrowsing.bind(this)
    );

    this._realtimeService.onUpdateQueuePosition(
      this.handleUpdateQueuePosition.bind(this)
    );
    this._realtimeService.onReceiveAgentTyping(
      this.handleReceiveTyping.bind(this)
    );
    this._realtimeService.onReceiveLanguageUpdate(
      this.handleLanguageUpdate.bind(this)
    );
    this._realtimeService.onConnect(this.handleConnect.bind(this));
    this._realtimeService.onReconnect(this.handleReconnect.bind(this));

    await this._realtimeService.startConnection();
    logInfo("connecting to signalR");
  }

  getMessages() {
    return this._messages;
  }

  isChatting() {
    return !!this._chatType;
  }

  async sendMessage(text: string) {
    const visitor = StoreContainer.VisitorStore.getVisitorInfo();

    const data: VisitorText = {
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      SiteId: StoreContainer.SettingStore.getSiteId(),
      Text: text,
      VisitorId: visitor.VisitorID
    };

    this.clearVisitorTypeahead();

    let chatLine: ChatLine;

    let message = {
      id: Date.now(),
      type: LineType.Text,
      status: MessageStatusType.Sending,
      timestamp: new Date(),
      lineCreator: LineCreator.Visitor,
      text: data.Text,
      rating: LineRating.Unset,
      headshotUrl: visitor.HeadshotUrl,
      author: visitor.DisplayName
    };

    // just a temporary message at this point for optimistic ui update

    const encodedMessage = this.escapeMessage(message);

    this._messages.push(encodedMessage);
    this._emitter.emit("receiveMessage", encodedMessage);

    try {
      chatLine = await EngagementService.sendVisitorText(data);
      const existingMessage = this._messages.filter(
        (x) => x.id === message.id
      )[0];
      const index = this._messages.indexOf(existingMessage);
      message = this.mapMessage(chatLine, MessageStatusType.Sent);
      this._messages[index] = message;
    } catch (e) {
      console.error(e);

      const existingMessage = this._messages.filter(
        (x) => x.id === message.id
      )[0];

      if (existingMessage) {
        const index = this._messages.indexOf(existingMessage);

        if (index > -1) {
          message.status = MessageStatusType.Failure;
          this._messages[index] = { ...message };
        }
      }
    }

    this._emitter.emit("receiveMessage", message);
  }

  /**
   * Fun history lesson about this method. We had a company report an XSS vulnerability
   * to us that didn't actually exist, but since optimistically rendered messages unsafely
   * execute what the visitor types in, you could essentially XSS attack yourself. We'll
   * just replace < and > with nothing while the optimistic message is visible to get rid
   * of the scary alert that pops up to make them think we fixed a vulnerability that didn't
   * really exist.
   */

  escapeMessage(message: { text: string }) {
    const escapedText = message.text.replace(/<|>/g, "");
    return { ...message, text: escapedText } as any;
  }

  async sendBotForm(data: any) {
    try {
      const result = await EngagementService.sendFormToBot(
        StoreContainer.SettingStore.getSiteId(),
        { engagementId: StoreContainer.EngagementStore.getEngagementId(), data }
      );
      await this.processBotResponse(result);
    } catch (e) {
      console.error(e);
      return;
    }
  }

  async sendBotMessage(
    type: VisitorBotMessageType,
    text: string,
    nextPath: string = null,
    sentiment: string = null
  ) {
    const visitorText: VisitorText = {
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      SiteId: StoreContainer.SettingStore.getSiteId(),
      Text: text,
      VisitorId: StoreContainer.VisitorStore.getVisitorId()
    };

    const payload: VisitorBotMessage = {
      VisitorText: visitorText,
      Type: type,
      NextPath: nextPath,
      Sentiment: sentiment
    };

    const visitor = StoreContainer.VisitorStore.getVisitorInfo();

    const optimisticMessageId = Date.now();
    if (VisitorBotMessageType.Progress && text) {
      const optimisticMessage = {
        id: optimisticMessageId,
        type: LineType.Text,
        status: MessageStatusType.Sending,
        timestamp: new Date(),
        lineCreator: LineCreator.Visitor,
        text: visitorText.Text,
        rating: LineRating.Unset,
        headshotUrl: visitor.HeadshotUrl,
        author: visitor.DisplayName,
        metadata: { nextPath }
      };

      // just a temporary message at this point for optimistic ui update

      this._messages.push(optimisticMessage);
      this._emitter.emit("receiveMessage", optimisticMessage);
    }

    let result = null;
    try {
      result = await EngagementService.sendMessageToBot(
        StoreContainer.SettingStore.getSiteId(),
        payload
      );
      await this.processBotResponse(result, optimisticMessageId);
    } catch (e) {
      console.error(e);
      const optimisticMessage = this._messages.find(
        (x) => x.id === optimisticMessageId
      );
      if (optimisticMessage) {
        optimisticMessage.status = MessageStatusType.Failure;
      }
      this._emitter.emit("receiveMessage", optimisticMessage);
      return;
    }
  }

  async processBotResponse(
    result: VisitorBotMessageResult,
    optimisticMessageId?: number
  ) {
    if (!result) {
      console.error("Bot didn't return a response!");
      return;
    }

    if (result.ErrorCode) {
      this.handleBotResponseError(result.ErrorCode);
      return;
    }

    if (result.VisitorName) {
      StoreContainer.VisitorStore.setVisitorName(result.VisitorName);
    }

    if (result.VisitorEmail) {
      StoreContainer.VisitorStore.setVisitorEmail(result.VisitorEmail);
    }

    if (result.VisitorPhone) {
      StoreContainer.VisitorStore.setVisitorPhone(result.VisitorPhone);
    }

    if (result.CustomData) {
      StoreContainer.SettingStore.setCustomVars(result.CustomData);
    }

    StoreContainer.BotStore.setSettings(result.BotState);

    this.processBotVisitorLine(optimisticMessageId, result.VisitorLine);
    await this.processBotResponseLines(result.Responses);

    StoreContainer.BotStore.setState(result.BotState);
    StoreContainer.BotStore.emitState();
    this._emitter.emit("chatAssignedToBot");

    await this.processBotStatus(StoreContainer.BotStore.getStatus());
  }

  private handleBotResponseError(error: string) {
    switch (error) {
      case "bot_not_found":
        console.error(
          "A bot was not found to handle this chat! Make sure your bot is created and enabled in the group we're trying to reach!"
        );
        break;
    }
  }

  private processBotVisitorLine(
    optimisticMessageId: number,
    visitorLine: ChatLine
  ) {
    if (!visitorLine) {
      return;
    }
    const message = this.mapMessage(visitorLine, MessageStatusType.Received);

    const optimisticMessageIndex = this._messages.findIndex(
      (x) => x.id === optimisticMessageId
    );
    if (optimisticMessageIndex > -1) {
      this._messages[optimisticMessageIndex] = message;
    } else {
      this._messages.push(message);
    }

    this._emitter.emit("receiveMessage", message);
  }

  private async processBotResponseLines(chatLines: ChatLine[]) {
    if (!chatLines) {
      return;
    }

    const messageSpeedInSeconds = StoreContainer.BotStore.getMessageSpeed();

    await forEachAsync(chatLines, async (chatLine) => {
      if (chatLine.LineCreator === LineCreator.Bot) {
        //Simulate a delay with typing indicator for the bot.
        if (StoreContainer.BotStore.botTypingIndicatorEnabled()) {
          this.handleReceiveTyping(this._agentName);
        }
        await sleep(messageSpeedInSeconds * 1000);

        this.handleReceiveLine(chatLine);
      } else {
        this.handleReceiveLine(chatLine);
      }
    });
  }

  private async processBotStatus(status: string) {
    switch (status) {
      case "normal":
      case "input":
        return;
      case "route-to-agent":
        EngagementService.sendBotFollowupRouteToAgent(
          StoreContainer.SettingStore.getSiteId(),
          {
            EngagementId: StoreContainer.EngagementStore.getEngagementId()
          }
        );
        return;
      case "route-to-group":
        EngagementService.sendBotFollowupRouteToGroup(
          StoreContainer.SettingStore.getSiteId(),
          {
            EngagementId: StoreContainer.EngagementStore.getEngagementId()
          }
        );
        return;
      case "closed":
        EngagementService.sendBotFollowupClose(
          StoreContainer.SettingStore.getSiteId(),
          {
            EngagementId: StoreContainer.EngagementStore.getEngagementId()
          }
        );
        this._emitter.emit("agentClose");
        return;
      case "webhook": {
        const result = await EngagementService.sendBotFollowupWebhook(
          StoreContainer.SettingStore.getSiteId(),
          {
            EngagementId: StoreContainer.EngagementStore.getEngagementId()
          }
        );
        await this.handleBotWebhookResult(result);
        return;
      }
      case "launchclicktocall": {
        await StoreContainer.BotStore.clearState();
        await StoreContainer.EngagementStore.beginClickToCall(
          StoreContainer.EngagementStore.getPrechatSurveyAnswers(),
          StoreContainer.VisitorStore.getVisitorPhone()
        );
        await StoreContainer.EngagementStore.clearEngagement();
        return;
      }
    }
  }

  async handleBotWebhookResult(result: WebhookResult) {
    if (!result) {
      console.error("Something went wrong with the bot webhook request");
      return;
    }

    await this.processBotResponseLines(result.Lines);

    await this.sendBotMessage(
      VisitorBotMessageType.Progress,
      null,
      result.NextPathId
    );
  }

  async emailVisitorTranscript(emailAddress: string) {
    await EngagementService.emailVisitorTranscript(
      StoreContainer.SettingStore.getSiteId(),
      StoreContainer.EngagementStore.getEngagementId(),
      emailAddress
    );
  }

  async setAgentRating(rating: number) {
    const data: RatingUpdate = {
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      VisitorId: StoreContainer.VisitorStore.getVisitorId(),
      Rating: rating
    };

    this._emitter.emit("changeAgentRating", rating);

    if (!data.EngagementId) {
      logError("missing engagementId when setting agent rating");
      return;
    }

    await EngagementService.setStarRating(
      StoreContainer.SettingStore.getSiteId(),
      data
    );
  }

  async setLineRating(lineId: number, rating: number) {
    if (!StoreContainer.EngagementStore.hasEngagementId()) {
      return;
    }
    const data: LineRatingUpdate = {
      Id: lineId,
      Rating: rating,
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      VisitorId: StoreContainer.VisitorStore.getVisitorId()
    };

    if (!data.EngagementId) {
      logError("missing engagementId when setting line rating");
      return;
    }

    await EngagementService.setLineRating(
      StoreContainer.SettingStore.getSiteId(),
      data
    );
  }

  async exitVisitor(wasChatting: boolean) {
    if (!StoreContainer.EngagementStore.hasEngagementId()) {
      return;
    }

    logInfo(`exiting visitor. was chatting: ${wasChatting}`);
    this.stopConnection();
    StoreContainer.CobrowseStore.requestStop();
    const data: ExitVisitorUpdate = {
      EngagementId: StoreContainer.EngagementStore.getEngagementId(),
      VisitorId: StoreContainer.VisitorStore.getVisitorId(),
      GroupId: StoreContainer.GroupStore.getActiveOrPageGroupId(),
      WasChatting: wasChatting
    };

    const result = await EngagementService.exitVisitor(
      StoreContainer.SettingStore.getSiteId(),
      data
    );
    if (result) {
      await this.handleReceiveEngagementContent(result);
    }
  }

  stopConnection() {
    if (!this._realtimeService) {
      return;
    }
    if (this._realtimeService.getExpectedState() !== RealtimeState.Connected) {
      return;
    }

    this._realtimeService.stopConnection();
    this._emitter.emit("disconnected");
  }

  async close() {
    this.clearChatInfo();
    await this.exitVisitor(false);
  }

  async closeChat() {
    const visitorHasMessages = !!this._messages.filter((message: Message) => {
      return message.lineCreator === LineCreator.Visitor;
    }).length;

    this.clearChatInfo();
    await this.exitVisitor(visitorHasMessages);
    StoreContainer.PostMessageStore.sendEndChatMessage();
  }

  clearChatInfo() {
    this._agentName = undefined;
    this._agentHeadshotUrl = undefined;
    this._agentRating = undefined;
    this._messages = [];
    this._chatType = null;
    StoreContainer.BotStore.clearState();
  }

  getSecureForm() {
    return this._secureForm;
  }

  getAgentRating() {
    return this._agentRating;
  }

  getAgentName() {
    return this._agentName;
  }

  getAgentHeadshotUrl() {
    return this._agentHeadshotUrl;
  }

  getCompanyLogoUrl() {
    return this._companyLogoUrl;
  }

  getQueuePosition() {
    return this._queuePosition;
  }

  getTimeInQueue() {
    return this._timeInQueue;
  }

  getEstimatedWait() {
    return this._estimatedWait;
  }

  onConnecting(callback: () => void) {
    this._emitter.addListener("connecting", callback);
  }

  onConnected(callback: () => void) {
    this._emitter.addListener("connected", callback);
  }

  onDisconnected(callback: () => void) {
    this._emitter.addListener("disconnected", callback);
  }

  onReceiveMessage(callback: (message: Message) => void) {
    this._emitter.addListener("receiveMessage", callback);
  }

  onChatQueued(callback: () => void) {
    this._emitter.addListener("chatQueued", callback);
  }

  onChatAssigned(callback: () => void) {
    this._emitter.addListener("chatAssigned", callback);
  }

  onChatAssignedToBot(callback: () => void) {
    this._emitter.addListener("chatAssignedToBot", callback);
  }

  onMessagesLoaded(callback: (messages: Array<Message>) => void) {
    this._emitter.addListener("messagesLoaded", callback);
  }

  onMessagesChanged(callback: (messages: Array<Message>) => void) {
    this._emitter.addListener("messagesChanged", callback);
  }

  onChangeAgentRating(callback: (rating: number) => void) {
    this._emitter.addListener("changeAgentRating", callback);
  }

  onUpdateQueuePosition(callback: (position: number) => void) {
    this._emitter.addListener("updateQueuePosition", callback);
  }

  onReceiveSecureForm(callback: (form: SecureForm) => void) {
    this._emitter.addListener("receivedSecureForm", callback);
  }

  onAgentClose(callback: () => void) {
    this._emitter.addListener("agentClose", callback);
  }

  onAgentsTypingChange(callback: (agentsTyping: string[]) => void) {
    this._emitter.addListener("agentsTypingChange", callback);
  }

  onReceiveCobrowseRequest(callback: (message: any) => void) {
    this._emitter.addListener("receiveCobrowseRequest", callback);
  }

  offReceiveCobrowseRequest(callback: (message: any) => void) {
    this._emitter.removeListener("receiveCobrowseRequest", callback);
  }

  onReceiveStopCobrowsing(callback: () => void) {
    this._emitter.addListener("receiveStopCobrowsing", callback);
  }

  onLoading(callback: () => void) {
    this._emitter.addListener("onLoading", callback);
  }

  removeAllListeners() {
    this._emitter.removeAllListeners();
  }

  handleVisitorTyping(message: string) {
    this.setVisitorTyping();
    this.sendTypeaheadThrottled(message);
  }

  clearVisitorTypeahead() {
    clearTimeout(this._typeaheadTimeout);
    this._typeaheadTimeout = null;
    this.sendTypeahead("");
  }

  sendTypeahead(message: string) {
    if (!StoreContainer.SettingStore.isVisitorTypeaheadEnabled()) {
      return;
    }

    const engagementId = StoreContainer.EngagementStore.getEngagementId();

    if (!engagementId) {
      return;
    }

    EngagementService.sendVisitorTypeahead(
      StoreContainer.SettingStore.getSiteId(),
      engagementId,
      message
    );
  }

  sendTypeaheadThrottled(message: string) {
    if (!this._typeaheadTimeout) {
      this._typeaheadTimeout = setTimeout(() => {
        this._typeaheadTimeout = null;
        this.sendTypeahead(message);
      }, 1000);
    }
  }

  async setVisitorTyping() {
    if (!this._visitorTypingTimeout) {
      this._visitorTypingTimeout = setTimeout(() => {
        this._visitorTypingTimeout = null;
      }, 3000);

      const data: EngagementUpdate = {
        EngagementId: StoreContainer.EngagementStore.getEngagementId(),
        VisitorId: StoreContainer.VisitorStore.getVisitorId()
      };

      if (!data.EngagementId) {
        logError("missing engagementId when trying to set typing");
        return;
      }

      await EngagementService.setVisitorTyping(
        StoreContainer.SettingStore.getSiteId(),
        data
      );
    }
  }

  //The engagement wasn't found. This is normal.
  //Typically this happens on page load and we want to simply clear engagement info and reset.
  //Sometimes it can happen during a chat if the visitor disconnects from signalR at the wrong time.
  //In these cases we want to simualte what they would have seen if they were connected.
  //The only issue with that is the engagement doesn't exist anymore
  //so we have to fake a "Chat ended" line for them so that it makes sense.
  async handleEngagementNotFound() {
    if (!this._chatType) {
      await StoreContainer.EngagementStore.clearEngagement();
    } else {
      const fakeLine: ChatLine = {
        DateCreated: new Date().toISOString(),
        EngagementID: StoreContainer.EngagementStore.getEngagementId(),
        LineType: LineType.ClosingMessage,
        //yeah this won't work for visitors in other languages. It's the best we can do given the situation
        Text: "This chat has been closed.",
        TextLanguage: "en",
        LineCreator: LineCreator.Notice,
        LineVisibility: LineVisibility.All,
        FromDisplay: null,
        EngagementUserID: null,
        HeadshotUrl: null,
        ID: null,
        IsSystemSignOutMessage: null,
        Metadata: null,
        MotionAIModule: null,
        OwnerLevel: null,
        Rating: null,
        UserID: null
      };
      this.handleReceiveLine(fakeLine);
    }
  }

  handleReceiveSecureForm(form: SecureForm) {
    if (form === undefined) {
      return;
    }
    this._secureForm = form;
    this._emitter.emit("receivedSecureForm");
  }

  async handleReceiveEngagementContent(content: EngagementContent) {
    StoreContainer.EngagementStore.setEngagementId(content.EngagementID);
    StoreContainer.EngagementStore.setPreEngagementId(undefined);

    StoreContainer.LanguageStore.setDisplayName(
      content.VisitorLanguageDisplayName
    );

    if (content.VisitorID) {
      StoreContainer.VisitorStore.setVisitorId(content.VisitorID); //if vm is disabled, our visitor id will come from request engagement
    }

    if (content.VisitorDisplayName) {
      StoreContainer.VisitorStore.setVisitorName(content.VisitorDisplayName);
    }

    if (content.VisitorEmail) {
      StoreContainer.VisitorStore.setVisitorEmail(content.VisitorEmail);
    }

    if (content.CustomData) {
      StoreContainer.SettingStore.setCustomVars(content.CustomData);
    }

    if (content.GroupID !== null) {
      await StoreContainer.EngagementStore.setEngagementGroupId(
        content.GroupID
      );
    }

    logInfo(`received engagement content: ${content.ContentType}`);

    switch (content.ContentType) {
      case EngagementContentType.Assigned:
        this._agentName = content.Content.AgentName;
        this._agentHeadshotUrl = content.Content.AgentHeadshotUrl;
        this._companyLogoUrl = content.Content.CompanyLogoUrl;
        this._agentRating = content.Content.Rating;
        this.handleReceiveSecureForm(content.Content.SecureForm);
        this._chatType = "Agent";
        this._emitter.emit("chatAssigned");
        StoreContainer.VisitorActionStore.addEngagement(
          StoreContainer.EngagementStore.getEngagementId(),
          content.Content.AgentName
        );
        this.connect();
        break;
      case EngagementContentType.BotChat:
        console.log("content:", content);
        this._agentName = content.Content.AgentName;
        this._agentHeadshotUrl = content.Content.ChatImageUrl;
        this._agentRating = content.Content.Rating;
        StoreContainer.BotStore.setSettings(content.BotState);
        StoreContainer.BotStore.setState(content.BotState);
        StoreContainer.BotStore.emitState();
        this._chatType = "Bot";
        this._emitter.emit("chatAssignedToBot");
        StoreContainer.VisitorActionStore.addEngagement(
          StoreContainer.EngagementStore.getEngagementId(),
          content.Content.AgentName
        );
        this.connect();
        await this.handleBotChat();
        await this.processBotStatus(StoreContainer.BotStore.getStatus());
        break;
      case EngagementContentType.FakeChat: // todo
        console.log("FakeChat", content);
        break;
      case EngagementContentType.KBSearch: // todo
        console.log("KBSearch", content);
        break;
      case EngagementContentType.Queued:
        this._queuePosition = content.Content.Position;
        this._timeInQueue = content.Content.TimeInQueue;
        this._estimatedWait = content.Content.EstimatedWaitTime;
        this._emitter.emit("chatQueued");
        this.connect();
        break;

      // If the agent rejects a queued chat, we'll end up here. It doesn't make
      // sense to have the app listening for these events on two separate stores,
      // so we'll call back down into the engagement store.

      //Including postchat in here for now since The EgnagementStore holds all of our survey content
      case EngagementContentType.PostChatSurvey:
      case EngagementContentType.UnavailableText:
      case EngagementContentType.UnavailableSurvey:
      case EngagementContentType.UnavailableUrl:
        this.stopConnection();

        //Don't move the visitor into a post chat state if they're currently "chatting"
        //We don't want to hide the chat convo until they're ready to close the chat themselves
        if (!this._chatType) {
          await StoreContainer.EngagementStore.handlePrechatEvent(
            content.ContentType,
            content.Content
          );
        } else {
          this._emitter.emit("chatAssigned");
          this._emitter.emit("agentClose");
        }
        break;
      default:
        throw new Error(`unhandled ContentType ${content.ContentType}`);
    }
  }

  testDisconnect() {
    this._realtimeService.testDisconnect();
  }

  testReconnect() {
    this._realtimeService.testReconnect();
  }

  emitMessagesChange() {
    this._emitter.emit("messagesChanged", this._messages);
  }

  async loadMessages() {
    if (!StoreContainer.EngagementStore.getEngagementId()) {
      return;
    }

    const chatLines = await EngagementService.loadMessages(
      StoreContainer.SettingStore.getSiteId(),
      StoreContainer.VisitorStore.getVisitorId(),
      StoreContainer.EngagementStore.getEngagementId()
    );

    if (!chatLines) {
      return;
    }

    // preserve any failed messages

    const failedMessages = this._messages.filter(
      (x) => x.status === MessageStatusType.Failure
    );

    this._messages = chatLines.map((chatLine) =>
      this.mapMessage(chatLine, MessageStatusType.Received)
    );

    // todo: if there were any failed messages, concat and re-order by date.

    if (failedMessages.length > 0) {
      this._messages = this._messages.concat(failedMessages).sort((a, b) => {
        return a.timestamp.getTime() - b.timestamp.getTime();
      });
    }

    this._emitter.emit("messagesLoaded", this._messages);
  }

  async resendMessage(message: Message, toBot: boolean) {
    const existingMessage = this._messages.filter(
      (x) => x.id === message.id
    )[0];
    const index = this._messages.indexOf(existingMessage);
    this._messages.splice(index, 1);
    if (toBot) {
      const nextPath = message.metadata && message.metadata.nextPath;
      await this.sendBotMessage(
        VisitorBotMessageType.Progress,
        message.text,
        nextPath
      );
    } else {
      await this.sendMessage(message.text);
    }
  }

  private handleReceiveLine(chatLine: ChatLine) {
    // The engagement hub sends out chatlines by visitor id. If the visitor
    // is in two engagements, they'll receive chatlines for both engagements.

    if (
      chatLine.EngagementID !== StoreContainer.EngagementStore.getEngagementId()
    ) {
      return;
    }

    if (this._messages.some((x) => x.id === chatLine.ID)) {
      return;
    }

    this.showUnreadMessageCount();
    const message = this.mapMessage(chatLine, MessageStatusType.Received);
    this._messages.push(message);
    this._emitter.emit("receiveMessage", message);
    this.clearTypingForAgent(chatLine.FromDisplay);

    if (chatLine.LineType === LineType.ClosingMessage) {
      this._emitter.emit("agentClose");
      StoreContainer.BotStore.clearState();
    }
  }

  private handleReceiveCobrowseRequest(message: any) {
    this._emitter.emit("receiveCobrowseRequest", message);
  }

  private handleStopCobrowsing() {
    this._emitter.emit("receiveStopCobrowsing");
  }

  private showUnreadMessageCount() {
    if (
      StoreContainer.SettingStore.getShowUnreadMessageCountInTitle() === false
    ) {
      return;
    }

    if (document.visibilityState !== "hidden") {
      return;
    }

    if (this._unreadMessageCount === 0) {
      this._previousTitle = this.getDocumentTitle();
    }

    this._unreadMessageCount++;
    this.setDocumentTitle(
      `(${this._unreadMessageCount}) ` + this._previousTitle
    );
  }

  private handleVisibilityChange() {
    if (document.visibilityState !== "hidden") {
      this._unreadMessageCount = 0;
      this.setDocumentTitle(this._previousTitle);
    }
  }

  private getDocumentTitle() {
    return window.parent.document.title;
  }

  private setDocumentTitle(title: string) {
    if (!title) {
      return;
    }
    window.parent.document.title = title;
  }

  private handleUpdateQueuePosition(position: number) {
    this._queuePosition = position;
    this._emitter.emit("updateQueuePosition", position);
  }

  private handleLanguageUpdate(message: {
    LanguageCode: string;
    LanguageDisplayName: string;
  }) {
    StoreContainer.LanguageStore.setDisplayName(message.LanguageDisplayName);
  }

  private handleReceiveTyping(agentName: string) {
    if (this._typingAgentTimers[agentName]) {
      clearTimeout(this._typingAgentTimers[agentName]);
    }

    this._typingAgentTimers[agentName] = setTimeout(() => {
      this.clearTypingForAgent(agentName);
    }, 4000);

    this._typingAgents[agentName] = {};
    this._emitter.emit("agentsTypingChange", this.getTypingAgents());
  }

  private async handleBotChat() {
    await this.loadMessages();

    //no status means the bot chat hasn't started yet
    if (!StoreContainer.BotStore.getStatus()) {
      this.sendBotMessage(VisitorBotMessageType.Start, null, null);
    }
  }

  private clearTypingForAgent(agentName: string) {
    if (!this._typingAgentTimers[agentName]) {
      return;
    }

    clearTimeout(this._typingAgentTimers[agentName]);
    delete this._typingAgentTimers[agentName];
    delete this._typingAgents[agentName];
    this._emitter.emit("agentsTypingChange", this.getTypingAgents());
  }

  private async handleConnect() {
    console.log("Connected. refreshing engagement...");
    if (this._chatType !== "Bot") {
      //Bots don't need this because the messages come
      //down in the api request instead of signalR
      await StoreContainer.EngagementStore.refreshEngagement();
    }
    this._emitter.emit("connected");
  }

  private handleReconnect() {
    console.log("Reconnected. reloading lines...");
    this.loadMessages();
  }

  private getTypingAgents() {
    return Object.keys(this._typingAgents);
  }

  private mapMessage(chatLine: ChatLine, status: MessageStatusType): Message {
    return {
      timestamp: new Date(chatLine.DateCreated),
      id: chatLine.ID,
      type: chatLine.LineType,
      text: chatLine.Text,
      lineCreator: chatLine.LineCreator,
      rating: chatLine.Rating,
      headshotUrl: chatLine.HeadshotUrl,
      author: chatLine.FromDisplay,
      status: status,
      metadata: JSON.parse(chatLine.Metadata)
    };
  }
}

export default ChatStore;
